/**
 * SlideCart Upsell Vendor Filter (robust, update-safe, low-lag)
 *
 * You reported:
 * - Previous solution didn't run reliably on updates
 * - Sometimes container exists only after SlideCart render
 * - Need to avoid lag + avoid infinite hide loop
 *
 * This version:
 * - Waits for the upsell container to exist (and re-attaches if SlideCart re-renders)
 * - Observes BOTH the container (when present) and document.body (to catch container replacement)
 * - Runs filtering in parallel with a small concurrency limit (fast, but not spiky)
 * - Disconnects the container observer while mutating DOM to prevent feedback loops
 * - Hides the entire upsell section while filtering, then reveals it
 * - Never keeps it hidden indefinitely (failsafe reveal timer)
 *
 * Configuration: set UPSELL_CONTAINER_SELECTOR to your confirmed class.
 */
(() => {
  'use strict';

  /*****************************************
   * CONFIG
   *****************************************/
  const BLOCKED_UPSELL_VENDOR = 'sale'; // hide upsells from this vendor (case-insensitive match)
  const DEBUG = false;

  // Confirmed container selector from you:
  const UPSELL_CONTAINER_SELECTOR = '#slidecarthq .upsells.upsells-stacked-container';

  // Upsell item selectors (themes differ). We try in order.
  const UPSELL_ITEM_SELECTORS = [
    '.upsell-item',
    '.upsell',                      // fallback
    '[data-upsell-item]',
    '[data-product-handle]'         // last resort
  ];

  // Hide entire section during filtering to avoid visible per-item lag
  const HIDE_SECTION_DURING_FILTER = true;

  // Debounce delay for DOM-driven updates
  const DEBOUNCE_MS = 80;

  // Vendor fetch concurrency (avoid "one at a time" but also avoid 20 parallel requests)
  const FETCH_CONCURRENCY = 6;

  // Failsafe: reveal section after this many ms even if something goes wrong
  const REVEAL_FAILSAFE_MS = 1200;

  /*****************************************
   * LOGGING
   *****************************************/
  const log  = (...a) => DEBUG && console.log('[upsell-filter]', ...a);
  const warn = (...a) => console.warn('[upsell-filter]', ...a);

  /*****************************************
   * STATE
   *****************************************/
  const vendorCache = new Map(); // handle -> vendorLower ('' allowed to avoid hot-loop)
  let hideTimer = null;
  let running = false;
  let queued = false;

  let revealFailsafe = null;

  // Observers
  const containerObserver = new MutationObserver(() => scheduleRun('container-mutation'));
  const bodyObserver = new MutationObserver(() => {
    // If SlideCart re-rendered and replaced the upsell container, reattach.
    ensureObservers();
    scheduleRun('body-mutation');
  });

  /*****************************************
   * HELPERS
   *****************************************/
  const lower = (v) => (v || '').toString().toLowerCase().trim();

  function findContainer() {
    return document.querySelector(UPSELL_CONTAINER_SELECTOR) || null;
  }

  function findUpsellNodes(container) {
    if (!container) return [];
    for (const sel of UPSELL_ITEM_SELECTORS) {
      const nodes = Array.from(container.querySelectorAll(sel));
      if (nodes.length) {
        // Prefer leaf-most nodes: if both ".upsell" wrappers and ".upsell-item" exist,
        // the array will come from the first selector with matches.
        return nodes;
      }
    }
    return [];
  }

  function readVendorFromNode(node) {
    return lower(
      node.getAttribute('data-vendor') ||
      node.getAttribute('data-product-vendor') ||
      node.dataset?.vendor ||
      node.dataset?.productVendor ||
      ''
    );
  }

  function extractHandle(node) {
    const dh =
      node.getAttribute('data-handle') ||
      node.dataset?.handle ||
      node.getAttribute('data-product-handle') ||
      node.dataset?.productHandle ||
      '';
    if (dh) return dh.toString().trim();

    const link = node.querySelector('a[href]');
    if (!link) return '';
    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      const m = url.pathname.match(/\/products\/([^/?#]+)/i);
      return m ? decodeURIComponent(m[1]) : '';
    } catch {
      return '';
    }
  }

  function hideSection(container) {
    if (!HIDE_SECTION_DURING_FILTER || !container) return;
    if (container.getAttribute('data-upsells-pending') === '1') return;

    container.style.visibility = 'hidden';
    container.style.pointerEvents = 'none';
    container.setAttribute('data-upsells-pending', '1');

    if (revealFailsafe) clearTimeout(revealFailsafe);
    revealFailsafe = setTimeout(() => {
      // failsafe reveal
      showSection(container);
    }, REVEAL_FAILSAFE_MS);
  }

  function showSection(container) {
    if (!HIDE_SECTION_DURING_FILTER || !container) return;

    if (revealFailsafe) {
      clearTimeout(revealFailsafe);
      revealFailsafe = null;
    }

    container.style.visibility = '';
    container.style.pointerEvents = '';
    container.removeAttribute('data-upsells-pending');
  }

  function applyHide(node) {
    // idempotent
    if (node.getAttribute('data-upsell-hidden') === '1') return false;
    node.style.display = 'none';
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('data-upsell-hidden', '1');
    return true;
  }

  async function fetchVendor(handle) {
    const h = (handle || '').toString().trim();
    if (!h) return '';
    if (vendorCache.has(h)) return vendorCache.get(h) || '';

    try {
      const res = await fetch(`/products/${encodeURIComponent(h)}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const v = lower(json.vendor);
      vendorCache.set(h, v);
      return v;
    } catch (e) {
      warn('vendor fetch failed', h, e);
      vendorCache.set(h, ''); // avoid hot loop
      return '';
    }
  }

  async function runWithConcurrency(items, worker, concurrency) {
    const results = new Array(items.length);
    let idx = 0;

    async function runner() {
      while (idx < items.length) {
        const cur = idx++;
        try {
          results[cur] = await worker(items[cur], cur);
        } catch (e) {
          results[cur] = undefined;
        }
      }
    }

    const n = Math.max(1, Math.min(concurrency || 1, items.length));
    await Promise.all(Array.from({ length: n }, runner));
    return results;
  }

  async function filterOnce(reason = 'manual') {
    const targetVendor = lower(BLOCKED_UPSELL_VENDOR);
    if (!targetVendor) return;

    const container = findContainer();
    if (!container) return;

    // Hide section immediately to eliminate perceived lag
    //hideSection(container);

    // Snapshot upsells
    const nodes = findUpsellNodes(container);
    if (!nodes.length) {
      //showSection(container);
      return;
    }

    // Build handle -> nodes map (dedupe work)
    const handleToNodes = new Map();
    const unknownNodes = [];

    for (const node of nodes) {
      const handle = extractHandle(node);
      if (!handle) {
        unknownNodes.push(node);
        continue;
      }
      if (!handleToNodes.has(handle)) handleToNodes.set(handle, []);
      handleToNodes.get(handle).push(node);
    }

    const handles = Array.from(handleToNodes.keys());

    // Resolve vendors:
    // - If DOM vendor is present on any node, use it for that handle (fast path)
    // - Otherwise fetch /products/<handle>.js with concurrency
    const handleVendor = new Map();

    for (const [h, list] of handleToNodes.entries()) {
      let v = '';
      for (const n of list) {
        const vDom = readVendorFromNode(n);
        if (vDom) { v = vDom; break; }
      }
      if (v) {
        handleVendor.set(h, v);
      } else if (vendorCache.has(h)) {
        handleVendor.set(h, vendorCache.get(h) || '');
      }
    }

    const missing = handles.filter(h => !handleVendor.has(h));
    if (missing.length) {
      await runWithConcurrency(
        missing,
        async (h) => {
          const v = await fetchVendor(h);
          handleVendor.set(h, v || '');
          return v;
        },
        FETCH_CONCURRENCY
      );
    }

    // Apply hides with observer temporarily disconnected to avoid feedback loops
    containerObserver.disconnect();

    try {
      let changed = 0;

      for (const [h, list] of handleToNodes.entries()) {
        const v = handleVendor.get(h) || '';
        if (v && v === targetVendor) {
          for (const node of list) {
            if (applyHide(node)) changed++;
          }
        }
      }

      // For nodes without handles, we can only hide if vendor is present in DOM
      for (const node of unknownNodes) {
        const vDom = readVendorFromNode(node);
        if (vDom && vDom === targetVendor) {
          if (applyHide(node)) changed++;
        }
      }

      log('filterOnce', { reason, nodes: nodes.length, handles: handles.length, missing: missing.length, changed });
    } finally {
      // Reattach observer to current container (in case it changed)
      attachContainerObserver();
      showSection(container);
    }
  }

  function scheduleRun(reason = 'debounced') {
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      run(reason).catch(e => warn('run failed', e));
    }, DEBOUNCE_MS);
  }

  async function run(reason) {
    if (running) {
      queued = true;
      return;
    }
    running = true;
    queued = false;

    try {
      await filterOnce(reason);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        scheduleRun('queued');
      }
    }
  }

  /*****************************************
   * OBSERVERS / HOOKS
   *****************************************/
  function attachContainerObserver() {
    const container = findContainer();
    if (!container) return;
    containerObserver.disconnect();
    containerObserver.observe(container, { childList: true, subtree: true });
    log('container observer attached');
  }

  function ensureObservers() {
    // Always keep body observer attached to detect container replacement
    if (!bodyObserver.__attached) {
      bodyObserver.observe(document.body, { childList: true, subtree: true });
      bodyObserver.__attached = true;
      log('body observer attached');
    }
    // Attach to container if present
    attachContainerObserver();
  }

  // Chain existing hooks instead of overwriting
  function chainHook(name, afterFn) {
    const prev = window[name];
    window[name] = function(...args) {
      let out;
      try {
        if (typeof prev === 'function') out = prev.apply(this, args);
      } finally {
        try { afterFn.apply(this, args); } catch (e) { warn(`${name} afterFn failed`, e); }
      }
      return out;
    };
  }

  /*****************************************
   * DEBUG API
   *****************************************/
  window.gwDebug = window.gwDebug || {};
  window.gwDebug.upsellFilter = {
    findContainer: () => findContainer(),
    scan: () => {
      const c = findContainer();
      const nodes = findUpsellNodes(c);
      return nodes.map(n => ({
        handle: extractHandle(n),
        vendorDom: readVendorFromNode(n),
        hidden: n.getAttribute('data-upsell-hidden') === '1',
        text: (n.textContent || '').trim().slice(0, 140)
      }));
    },
    run: () => run('manual'),
    cache: () => Object.fromEntries(vendorCache.entries())
  };

  /*****************************************
   * BOOTSTRAP
   *****************************************/
  function init() {
    ensureObservers();

    // Run once now (will no-op if container not present yet)
    scheduleRun('init');

    // Chain SlideCart hooks if present (run after original)
    chainHook('SLIDECART_UPDATED', () => {
      ensureObservers();
      // Delay slightly so SlideCart DOM has time to render
      requestAnimationFrame(() => scheduleRun('SLIDECART_UPDATED'));
    });
    chainHook('SLIDECART_OPENED', () => {
      ensureObservers();
      requestAnimationFrame(() => scheduleRun('SLIDECART_OPENED'));
    });

    // Defensive custom events
    document.addEventListener('slidecart:updated', () => scheduleRun('slidecart:updated'), { passive: true });
    document.addEventListener('slidecart:opened', () => scheduleRun('slidecart:opened'), { passive: true });

    // If container appears later, a body mutation will trigger ensureObservers + scheduleRun
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
